import { graphql } from 'react-relay';
import { useContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import uuid from 'uuid/v4';
import unionBy from 'lodash/unionBy';
import debounce from 'lodash/debounce';

import { createFilter } from '@atlaskit/react-select/filters';
import type { GroupedOptionsType } from '@atlaskit/select/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { MutationUpdaterFn } from '@confluence/apollo-relay-interop/entry-points/apolloTypes.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import {
	LABELS_SEARCH_LABEL_EXPERIENCE,
	LABELS_RECOMMENDED_EXPERIENCE,
	LABELS_ADD_LABEL_EXPERIENCE,
	LABELS_CREATE_LABEL_EXPERIENCE,
	LABELS_DELETE_LABEL_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { isViewPage, isBlogPage } from '@confluence/named-routes/entry-points/routes-utils.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useLazyQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useLazyQueryInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';

import { fg } from '@confluence/feature-gating';

import { parseErrorMessage } from '../helpers.js';
import { ALLOWED_CONTENT_STATUSES, ActionTypes, INVALID_CHARACTERS } from '../constants.tsx';
import { useExternalLabels, buildExternalLabelsAri } from '../ManagedLabels/useExternalLabels.ts';
import { useAvailableManagedLabels } from '../ManagedLabels/useAvailableManagedLabels.ts';
import { useApplyManagedLabel } from '../ManagedLabels/useApplyManagedLabel.ts';
import { useManagedLabels } from '../ManagedLabels/useManagedLabels.ts';

import {
	type useLabelsSelectPropsQuery$data,
	type useLabelsSelectPropsQuery$variables,
} from './__generated__/useLabelsSelectPropsQuery.graphql.ts';
import type { ViewPageLabelsContentFragment_content$data } from './__generated__/ViewPageLabelsContentFragment_content.graphql.ts';
import type {
	useLabelsSelectPropsRecommendedLabelsQuery$data,
	useLabelsSelectPropsRecommendedLabelsQuery$variables,
} from './__generated__/useLabelsSelectPropsRecommendedLabelsQuery.graphql.ts';
import type {
	useLabelsSelectPropsLabelSearchQuery$data,
	useLabelsSelectPropsLabelSearchQuery$variables,
} from './__generated__/useLabelsSelectPropsLabelSearchQuery.graphql.ts';
import type {
	useLabelsSelectPropsDeleteLabelMutation$data,
	useLabelsSelectPropsDeleteLabelMutation$variables,
} from './__generated__/useLabelsSelectPropsDeleteLabelMutation.graphql.ts';
import type {
	useLabelsSelectPropsAddLabelsMutation$data,
	useLabelsSelectPropsAddLabelsMutation$variables,
} from './__generated__/useLabelsSelectPropsAddLabelsMutation.graphql.ts';
import type {
	useLabelsSelectPropsSSRLabelsQuery$data,
	useLabelsSelectPropsSSRLabelsQuery$variables,
} from './__generated__/useLabelsSelectPropsSSRLabelsQuery.graphql.ts';
import type { CustomOptionType } from './LabelsSelect.tsx';
import {
	buildAvailableManagedOptions,
	buildGroupedLabelOptions,
	isManagedEligibleContentType,
	transformAppliedManagedToOptions,
} from './managedLabelsPickerOptions.ts';
import {
	useManagedLabelsOverlay,
	mergeManagedLabelsWithOverlay,
	optimisticallyApplyManagedLabel,
	optimisticallyRemoveManagedLabel,
	rollbackApplyManagedLabel,
	rollbackRemoveManagedLabel,
	reconcileManagedLabelsOverlay,
} from '../ManagedLabels/managedLabelsOptimisticStore.ts';

const useLabelsSelectPropsQuery = graphql`
	query useLabelsSelectPropsQuery($contentId: ID!, $status: [String]) {
		content(id: $contentId, status: $status) {
			nodes {
				...ViewPageLabelsContentFragment_content @relay(mask: false)
			}
		}
	}
`;

const useLabelsSelectPropsSSRLabelsQuery = graphql`
	query useLabelsSelectPropsSSRLabelsQuery($contentId: ID!) {
		content(id: $contentId) {
			nodes {
				...ViewPageLabelsContentFragment_content @relay(mask: false)
			}
		}
	}
`;
const useLabelsSelectPropsAddLabelsMutation = graphql`
	mutation useLabelsSelectPropsAddLabelsMutation($contentId: ID!, $labels: [LabelInput!]!) {
		addLabels(input: { contentId: $contentId, labels: $labels }) {
			__typename
			labels {
				__typename
				count
				nodes {
					__typename
					id
					prefix
					name
					label
				}
			}
		}
	}
`;
const useLabelsSelectPropsDeleteLabelMutation = graphql`
	mutation useLabelsSelectPropsDeleteLabelMutation($contentId: ID!, $label: String!) {
		deleteLabel(input: { contentId: $contentId, label: $label }) {
			contentId
			label
		}
	}
`;
const useLabelsSelectPropsLabelSearchQuery = graphql`
	query useLabelsSelectPropsLabelSearchQuery($contentId: ID!, $searchText: String!) {
		contentLabelSearch(contentId: $contentId, searchText: $searchText) {
			suggestedLabels {
				prefix
				name
				id
				label
			}
			otherLabels {
				prefix
				name
				id
				label
			}
		}
	}
`;
const useLabelsSelectPropsRecommendedLabelsQuery = graphql`
	query useLabelsSelectPropsRecommendedLabelsQuery($contentId: ID!, $spaceId: ID!) {
		getRecommendedLabels(entityId: $contentId, entityType: "page", spaceId: $spaceId) {
			recommendedLabels {
				id
				name
				namespace
				strategy
			}
		}
	}
`;
const i18n = defineMessages({
	suggestedLabels: {
		id: 'labels.use-labels-select-props.suggested-labels',
		defaultMessage: 'Suggested labels',
		description:
			'heading for options in labels select that are recommended by smarts label service',
	},
	managedLabelsGroup: {
		id: 'labels.use-labels-select-props.managed-labels-group',
		defaultMessage: 'Managed labels',
		description:
			'Heading for the group of admin-managed (external) labels in the labels selector dropdown',
	},
	userLabelsGroup: {
		id: 'labels.use-labels-select-props.user-labels-group',
		defaultMessage: 'User labels',
		description:
			'Heading for the group of regular user-created labels in the labels selector dropdown, shown alongside the managed labels group',
	},
});

const getValidationError = (inputValue: string) => {
	const parsedInputValue = inputValue.replace(/^my:/, '');
	if (parsedInputValue.match(INVALID_CHARACTERS)) {
		// list taken from monolith here: com/atlassian/confluence/labels/LabelParser.java:39
		return 'labelsValidationInvalidCharacter';
	} else if (parsedInputValue.match(/^~/)) {
		return 'labelsValidationTilde';
	}
	if (parsedInputValue.length > 255) {
		return 'labelsValidationLength';
	}
	return '';
};

const isLabelNotFoundError = (error) =>
	/No label found with name/.test(parseErrorMessage(error).message);

const isContentNotFoundError = (error) =>
	/No content found with id/.test(parseErrorMessage(error).message);

const isLabelDeletePermissionsError = (error) =>
	/Could not delete label from content with id/.test(parseErrorMessage(error).message);

const isLabelMutationLoggedOutError = (error) =>
	/Current user not permitted to use Confluence/.test(parseErrorMessage(error).message);

const isLabelSearch403Error = (error) =>
	/Unexpected status code 403/.test(parseErrorMessage(error).message);

export const getPrivacySafePrefix = (prefix = ''): string | undefined =>
	prefix.match(/my|global|team/)?.[0];

export const isStarredLabel: (label: string) => boolean = (label) =>
	label === 'my:favourite' || label === 'my:favorite';

const filterByPrefix = (labels, prefix) =>
	(labels || []).filter((label) => label?.prefix === prefix);

const transformToPrefixedLabel = (prefix, label) =>
	`${prefix && prefix !== 'global' ? `${prefix}:` : ''}${label}`;

const sanitizeInputValue = (input: string) => {
	return input.toLowerCase().replace(/\s/g, '-');
};

const getRecommendedLabelsFromData = (data) => data?.getRecommendedLabels?.recommendedLabels;

const makeTransformLabelToOption = (strategy) => (label) => {
	const prefixedLabel = transformToPrefixedLabel(label.prefix, label.label);
	return {
		id: label.id,
		label: prefixedLabel,
		value: prefixedLabel,
		prefix: label.prefix,
		name: label.name,
		strategy,
	};
};

const transformRecommendedLabelToOption = (label) => {
	const prefixedLabel = transformToPrefixedLabel(label.namespace, label.name);
	return {
		id: label.id,
		label: prefixedLabel,
		value: prefixedLabel,
		prefix: label.namespace,
		name: label.name,
		strategy: 'recommended',
		subStrategy: label.strategy,
	};
};

const transformCurrentLabelsToSelectedOptions = (currentLabels): CustomOptionType[] => {
	if (!currentLabels) {
		return [];
	}
	return (currentLabels?.content?.nodes?.[0]?.labels?.nodes || [])
		.map(makeTransformLabelToOption('current'))
		.filter(({ label }) => !isStarredLabel(label));
};

const useOptions = ({ searchLabelQueryResults, recommendedLabelsQueryResults, hasInput }) => {
	const intl = useIntl();
	return useMemo(() => {
		if (hasInput) {
			const otherLabelOptions = (
				searchLabelQueryResults?.contentLabelSearch?.otherLabels || []
			).map(makeTransformLabelToOption('searched_other'));

			const suggestedLabelOptions = (
				searchLabelQueryResults?.contentLabelSearch?.suggestedLabels || []
			).map(makeTransformLabelToOption('searched_suggested'));
			return [...suggestedLabelOptions, ...otherLabelOptions];
		} else {
			const recommendedLabelOptions = (
				getRecommendedLabelsFromData(recommendedLabelsQueryResults) || []
			).map(transformRecommendedLabelToOption);
			return [
				{
					label: intl.formatMessage(i18n.suggestedLabels),
					options: recommendedLabelOptions,
				},
			];
		}
	}, [searchLabelQueryResults, recommendedLabelsQueryResults, hasInput, intl]);
};

type LabelsSelectPropsType = ({
	contentId,
	source,
	onUpdateSuccess,
	onAdded,
	inheritedLabelsData,
}: {
	contentId: string;
	source: string;
	onUpdateSuccess?: () => void;
	onAdded?: () => void;
	inheritedLabelsData?: ViewPageLabelsContentFragment_content$data | null;
}) => {
	options: GroupedOptionsType<CustomOptionType>;
	filterOption: (option: any, inputValue: string) => boolean;
	value: CustomOptionType[];
	onChange: (label: any, action: any) => any;
	onMenuOpen: () => void;
	onInputChange: (inputValue: string) => void;
	inputValue: string;
	isLoading: boolean;
	isDisabled?: boolean;
	labelsQueryLoading: boolean;
	labelsQueryError?: ApolloErrorInterop;
	labelsMutationError?: ApolloErrorInterop;
	validationError: string;
	labelsSearchQueryError?: ApolloErrorInterop;
	fetchRecommendedLabels: () => void;
};

export const useLabelsSelectProps: LabelsSelectPropsType = ({
	contentId,
	source,
	onUpdateSuccess,
	onAdded,
	inheritedLabelsData,
}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isLivePage = useIsCurrentPageLive();

	const experienceTracker = useContext(ExperienceTrackerContext);
	const { activationId, cloudId, orgId } = useSessionData();
	const [validationError, setValidationError] = useState('');
	const [inputValue, setInputValue] = useState('');
	const [localInheritedLabelsData, setLocalInheritedLabelsData] = useState<
		ViewPageLabelsContentFragment_content$data | null | undefined
	>();
	const isLabelsPreloadedFromContent = Boolean(
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		(window.__SSR_RENDERED__ && (isViewPage() || isBlogPage())) || process.env.REACT_SSR,
	);
	const hasInheritedLabelsData = inheritedLabelsData !== undefined;
	const inheritedLabelsDataForRead = localInheritedLabelsData ?? inheritedLabelsData;

	const {
		data: ssrLabelsQueryData,
		loading: ssrLabelsQueryLoading,
		error: ssrLabelsQueryError,
	} = useQueryInterop<
		useLabelsSelectPropsSSRLabelsQuery$data,
		useLabelsSelectPropsSSRLabelsQuery$variables
	>(useLabelsSelectPropsSSRLabelsQuery, {
		variables: {
			contentId,
		},
		skip: hasInheritedLabelsData || !isLabelsPreloadedFromContent,
	});
	const {
		data: labelsQueryData,
		loading: labelsQueryLoading,
		error: labelsQueryError,
	} = useQueryInterop<useLabelsSelectPropsQuery$data, useLabelsSelectPropsQuery$variables>(
		useLabelsSelectPropsQuery,
		{
			variables: {
				contentId,
				status: ALLOWED_CONTENT_STATUSES,
			},
			skip: hasInheritedLabelsData || isLabelsPreloadedFromContent,
			fetchPolicy: 'cache-and-network',
		},
	);

	const labelData = useMemo(() => {
		if (hasInheritedLabelsData) {
			return inheritedLabelsDataForRead
				? {
						content: {
							nodes: [inheritedLabelsDataForRead],
						},
					}
				: null;
		}
		if (isLabelsPreloadedFromContent) {
			if (!ALLOWED_CONTENT_STATUSES.includes(ssrLabelsQueryData?.content?.nodes?.[0]?.status || ''))
				return null;
			return ssrLabelsQueryData;
		}
		return labelsQueryData;
	}, [
		hasInheritedLabelsData,
		inheritedLabelsDataForRead,
		ssrLabelsQueryData,
		labelsQueryData,
		isLabelsPreloadedFromContent,
	]);

	const selectedOptions = useMemo(
		() => transformCurrentLabelsToSelectedOptions(labelData),
		[labelData],
	);

	const type = labelData?.content?.nodes?.[0]?.type;
	const spaceId = labelData?.content?.nodes?.[0]?.space?.id || '';
	// this will be used to disable add/delete if the user doesn't have page edit permissions
	const isDisabled = !labelData?.content?.nodes?.[0]?.operations?.find((item) => {
		return item?.operation === 'update' && item?.targetType === type;
	});

	// --- Admin-managed (external) labels ---------------------------------------
	// The read/available hooks self-skip when the gate is off, so calling them
	// unconditionally is safe.
	const managedEnabled =
		fg('confluence_managed_external_labels_endpoint') && isManagedEligibleContentType(type);
	const { labels: appliedManagedLabels, refetch: refetchAppliedManaged } =
		useExternalLabels(contentId);
	const { availableLabels: availableManagedLabels } = useAvailableManagedLabels();
	const { applyLabel: applyManagedLabel } = useApplyManagedLabel();
	const { removeManagedLabel } = useManagedLabels(contentId);
	const managedEntityAri = buildExternalLabelsAri(cloudId, contentId);
	const managedOrgAri = orgId ? `ari:cloud:platform::org/${orgId}` : '';
	const managedWorkspaceAri = cloudId ? `ari:cloud:confluence:${cloudId}:site/${cloudId}` : '';

	// Optimistic overlay so apply/remove feels instant instead of waiting on the
	// assign/unassign round-trip plus the follow-up `confluence_externalLabels`
	// re-read. Shared cross-surface via a module-level store keyed by contentId; it
	// reconciles via the effect below and rolls back on mutation failure.
	const managedOverlay = useManagedLabelsOverlay(contentId);

	const selectedManagedOptions = useMemo(() => {
		if (!managedEnabled) {
			return [];
		}
		return transformAppliedManagedToOptions(
			mergeManagedLabelsWithOverlay(appliedManagedLabels, managedOverlay),
		);
	}, [managedEnabled, appliedManagedLabels, managedOverlay]);

	// Reconcile the overlay against the server read: drop applied entries the server
	// now includes, and removed entries the server no longer includes.
	useEffect(() => {
		const serverIds = new Set(
			appliedManagedLabels.map((label) => label.id).filter((id): id is string => Boolean(id)),
		);
		reconcileManagedLabelsOverlay(contentId, serverIds);
	}, [contentId, appliedManagedLabels]);

	const orgHasManagedLabels =
		managedEnabled && (availableManagedLabels.length > 0 || appliedManagedLabels.length > 0);

	useEffect(() => {
		!(labelsQueryLoading || ssrLabelsQueryLoading) &&
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'rendered',
					actionSubject: 'labels',
					containerId: spaceId,
					containerType: 'space',
					objectId: contentId,
					objectType: type,
					source,
					attributes: {
						contentType: type,
						count: {
							global: filterByPrefix(selectedOptions, 'global').length,
							personal: filterByPrefix(selectedOptions, 'my').length,
							total: selectedOptions.length,
						},
						...(isLivePage ? { isLivePage } : null),
					},
				},
			}).fire();
	}, [
		labelsQueryLoading,
		ssrLabelsQueryLoading,
		selectedOptions,
		spaceId,
		contentId,
		type,
		source,
		createAnalyticsEvent,
		isLivePage,
	]);
	const [addLabel, { error: addError }] = useMutationInterop<
		useLabelsSelectPropsAddLabelsMutation$data,
		useLabelsSelectPropsAddLabelsMutation$variables
	>(useLabelsSelectPropsAddLabelsMutation, {
		update(_, { data }) {
			if (hasInheritedLabelsData) {
				setLocalInheritedLabelsData((currentData) => {
					const currentInheritedData = currentData ?? inheritedLabelsData;

					return currentInheritedData && data?.addLabels?.labels
						? {
								...currentInheritedData,
								labels: data.addLabels.labels,
							}
						: currentInheritedData;
				});
			}
			const _cachedData = readQueryInterop<
				useLabelsSelectPropsQuery$data,
				useLabelsSelectPropsQuery$variables
			>({
				query: useLabelsSelectPropsQuery,
				variables: {
					contentId,
					status: ALLOWED_CONTENT_STATUSES,
				},
			});
			const cachedData = JSON.parse(JSON.stringify(_cachedData)); // converting read-only to mutable
			if (cachedData?.content?.nodes?.[0] && data?.addLabels?.labels) {
				const newLabels = unionBy(
					cachedData.content.nodes[0].labels?.nodes,
					data.addLabels?.labels.nodes,
					'id',
				);
				cachedData.content.nodes[0].labels = {
					count: newLabels.length,
					nodes: newLabels,
					// @ts-ignore
					__typename: 'PaginatedLabelList',
				};
				writeQueryInterop({
					query: useLabelsSelectPropsQuery,
					variables: {
						contentId,
						status: ALLOWED_CONTENT_STATUSES,
					},
					data: cachedData,
				});
			}
		},
		onCompleted: () => {
			if (onUpdateSuccess) {
				onUpdateSuccess();
			}
			if (onAdded) {
				onAdded();
			}
		},
	});
	const handleDeleteLabelUpdate: MutationUpdaterFn<useLabelsSelectPropsDeleteLabelMutation$data> =
		useCallback(
			(_, { data }) => {
				const cachedData = readQueryInterop<
					useLabelsSelectPropsQuery$data,
					useLabelsSelectPropsQuery$variables
				>({
					query: useLabelsSelectPropsQuery,
					variables: {
						contentId,
						status: ALLOWED_CONTENT_STATUSES,
					},
				});

				if (
					cachedData?.content?.nodes?.[0]?.labels?.nodes &&
					cachedData?.content?.nodes?.[0]?.labels?.count != null
				) {
					// TODO: label is not a unique identifier. Either prefix + label or label id should be used instead, but neither are returned by the mutation
					const filteredCachedLabels = cachedData.content.nodes[0].labels.nodes.filter(
						(node) => node?.label !== data?.deleteLabel?.label,
					);
					if (hasInheritedLabelsData) {
						setLocalInheritedLabelsData((currentData) => {
							const currentInheritedData = currentData ?? inheritedLabelsData;

							return currentInheritedData?.labels
								? (() => {
										const filteredLabels =
											currentInheritedData.labels.nodes?.filter(
												(node) => node?.label !== data?.deleteLabel?.label,
											) || [];

										return {
											...currentInheritedData,
											labels: {
												...currentInheritedData.labels,
												nodes: filteredLabels,
												count: filteredLabels.length,
											},
										};
									})()
								: currentInheritedData;
						});
					}
					writeQueryInterop<useLabelsSelectPropsQuery$data, useLabelsSelectPropsQuery$variables>({
						query: useLabelsSelectPropsQuery,
						variables: {
							contentId,
							status: ALLOWED_CONTENT_STATUSES,
						},
						data: {
							content: {
								nodes: [
									{
										...cachedData.content.nodes[0],
										labels: {
											nodes: filteredCachedLabels,
											count: filteredCachedLabels.length,
											// @ts-ignore
											__typename: 'PaginatedLabelList',
										},
									},
								],
								__typename: 'PaginatedContentListWithChild',
							},
						},
					});
				}
			},
			[contentId, hasInheritedLabelsData, inheritedLabelsData],
		);

	const [deleteLabel, { error: rawDeleteError }] = useMutationInterop<
		useLabelsSelectPropsDeleteLabelMutation$data,
		useLabelsSelectPropsDeleteLabelMutation$variables
	>(useLabelsSelectPropsDeleteLabelMutation, {
		update: handleDeleteLabelUpdate,
		onCompleted: () => {
			if (onUpdateSuccess) {
				onUpdateSuccess();
			}
		},
	});
	const deleteError = !isLabelNotFoundError(rawDeleteError) ? rawDeleteError : undefined;

	const [
		searchQuery,
		{
			data: searchLabelQueryResults,
			loading: searchLabelQueryLoading,
			error: searchLabelQueryError,
		},
	] = useLazyQueryInterop<
		useLabelsSelectPropsLabelSearchQuery$data,
		useLabelsSelectPropsLabelSearchQuery$variables
	>(useLabelsSelectPropsLabelSearchQuery, {
		onCompleted: () => {
			experienceTracker.succeed({
				name: LABELS_SEARCH_LABEL_EXPERIENCE,
			});
		},
		onError: (error) => {
			if (isLabelSearch403Error(error)) {
				markErrorAsHandled(error);
				experienceTracker.abort({
					name: LABELS_SEARCH_LABEL_EXPERIENCE,
					reason: 'User was logged out',
				});
			}
			experienceTracker.fail({
				error,
				name: LABELS_SEARCH_LABEL_EXPERIENCE,
			});
		},
	});
	const [
		recommendedLabelsQuery,
		{ data: recommendedLabelsQueryResults, loading: recommendedLabelsQueryLoading },
	] = useLazyQueryInterop<
		useLabelsSelectPropsRecommendedLabelsQuery$data,
		useLabelsSelectPropsRecommendedLabelsQuery$variables
	>(useLabelsSelectPropsRecommendedLabelsQuery, {
		variables: {
			contentId,
			spaceId,
		},
		onCompleted: (data) => {
			experienceTracker.succeed({
				name: LABELS_RECOMMENDED_EXPERIENCE,
			});
			const recommendedLabels = getRecommendedLabelsFromData(data);
			recommendedLabels &&
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'shown',
						actionSubject: 'labelRecommendations',
						source,
						containerId: spaceId,
						containerType: 'space',
						objectId: contentId,
						objectType: type,
						attributes: {
							contentType: type,
							count: recommendedLabels?.length,
						},
					},
				}).fire();
		},
		onError: (error) => {
			experienceTracker.fail({
				error,
				name: LABELS_RECOMMENDED_EXPERIENCE,
			});
		},
	});
	const fetchRecommendedLabels = useCallback(() => {
		if (type === 'page' && !isDisabled) {
			experienceTracker.start({
				name: LABELS_RECOMMENDED_EXPERIENCE,
			});
			recommendedLabelsQuery();
		}
	}, [type, experienceTracker, recommendedLabelsQuery, isDisabled]);

	const recommendedLabels = getRecommendedLabelsFromData(recommendedLabelsQueryResults);
	const userOptions = useOptions({
		searchLabelQueryResults,
		recommendedLabelsQueryResults,
		hasInput: Boolean(inputValue),
	});

	// Managed group options; only computed when managed is in play so non-managed
	// surfaces keep the exact existing option list.
	const managedGroupOptions = useMemo(
		() =>
			managedEnabled
				? buildAvailableManagedOptions({
						availableManaged: availableManagedLabels,
						appliedManaged: appliedManagedLabels,
						inputValue,
					})
				: [],
		[managedEnabled, availableManagedLabels, appliedManagedLabels, inputValue],
	);

	const options = useMemo(
		() =>
			buildGroupedLabelOptions({
				managedEnabled,
				orgHasManagedLabels,
				managedGroupOptions,
				userOptions,
				managedGroupHeading: intl.formatMessage(i18n.managedLabelsGroup),
				userGroupHeading: intl.formatMessage(i18n.userLabelsGroup),
			}),
		[managedEnabled, orgHasManagedLabels, managedGroupOptions, userOptions, intl],
	);

	const debouncedSearchQuery = useMemo(
		() => debounce(searchQuery, 500, { leading: true }),
		[searchQuery],
	);

	const handleInputChange = (inputValue) => {
		const error = getValidationError(inputValue);
		setValidationError(error);
		setInputValue(sanitizeInputValue(inputValue));
		if (inputValue) {
			experienceTracker.start({ name: LABELS_SEARCH_LABEL_EXPERIENCE });
			if (!validationError) {
				debouncedSearchQuery({
					variables: {
						searchText: inputValue,
						contentId,
					},
				});
			}
		} else {
			fetchRecommendedLabels();
		}
	};

	const filterOption = ({ value }) => {
		return (
			!searchLabelQueryError &&
			createFilter({}) &&
			!isStarredLabel(value) &&
			!getValidationError(value)
		);
	};

	const fireLabelChangeEvent = useCallback(
		({ action, labels }: any) => {
			labels.map(({ id: labelId, name: labelName, prefix: labelPrefix, strategy, subStrategy }) => {
				const recommendedIndex = recommendedLabels?.findIndex(({ id }) => id === labelId);
				let recommendedRanking;
				if (recommendedIndex >= 0) {
					recommendedRanking = recommendedIndex;
					if (strategy?.startsWith('searched')) {
						strategy = 'recommended_searched';
						subStrategy = recommendedLabels[recommendedIndex].strategy;
					}
				}
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action,
						actionSubject: 'label',
						source,
						objectType: type,
						objectId: contentId,
						containerType: 'space',
						containerId: spaceId,
						attributes: {
							contentId,
							contentType: type,
							labelId,
							strategy,
							subStrategy,
							isNewLabel: strategy === 'newly_created',
							privacySafeLabelPrefix: getPrivacySafePrefix(labelPrefix),
							activationId,
							recommendedCount: recommendedLabels?.length,
							hasRecommendations: !!recommendedLabels?.length,
							recommendedRanking,
							...(isLivePage ? { isLivePage } : null),
						},
						nonPrivacySafeAttributes: {
							labelName,
							labelPrefix,
						},
					},
				}).fire();
			});
		},
		[
			type,
			source,
			contentId,
			spaceId,
			createAnalyticsEvent,
			activationId,
			recommendedLabels,
			isLivePage,
		],
	);

	const handleSelectLabel = useCallback(
		async (option: any) => {
			experienceTracker.start({
				name: LABELS_ADD_LABEL_EXPERIENCE,
			});
			try {
				setInputValue('');

				// const optimisticResponse = J

				await addLabel({
					variables: {
						labels: [{ name: option.name, prefix: option.prefix }],
						contentId,
					},
					optimisticResponse: {
						addLabels: {
							__typename: 'AddLabelsPayload',
							labels: {
								__typename: 'PaginatedLabelList',
								count: (labelData?.content?.nodes?.[0]?.labels?.count || 0) + 1,
								nodes: [
									...(labelData?.content?.nodes?.[0]?.labels?.nodes || []),
									{
										__typename: 'Label',
										id: option.id,
										prefix: option.prefix,
										name: option.name,
										label: option.name,
									},
								],
							},
						},
					},
				});
				experienceTracker.succeed({ name: LABELS_ADD_LABEL_EXPERIENCE });
				fireLabelChangeEvent({
					action: 'add',
					labels: [option],
				});
			} catch (error) {
				experienceTracker.fail({
					error,
					name: LABELS_ADD_LABEL_EXPERIENCE,
				});
			}
		},
		[experienceTracker, addLabel, labelData, fireLabelChangeEvent, contentId],
	);

	const handleCreateLabel = useCallback(
		async (option: any) => {
			experienceTracker.start({
				name: LABELS_CREATE_LABEL_EXPERIENCE,
			});
			try {
				setInputValue('');
				const { data } = await addLabel({
					variables: {
						labels: [{ name: option.value, prefix: '' }],
						contentId,
					},
					optimisticResponse: {
						addLabels: {
							__typename: 'AddLabelsPayload',
							labels: {
								__typename: 'PaginatedLabelList',
								count: (labelData?.content?.nodes?.[0]?.labels?.count || 0) + 1,
								nodes: [
									...(labelData?.content?.nodes?.[0]?.labels?.nodes || []),
									{
										name: option.value,
										prefix: '',
										__typename: 'Label',
										id: uuid(),
										label: option.value,
									},
								],
							},
						},
					},
				});
				experienceTracker.succeed({ name: LABELS_CREATE_LABEL_EXPERIENCE });
				fireLabelChangeEvent({
					action: 'add',
					// filter out the newly added labels from the already existing - this is necessary in order to get the new label ids to send in the analytics event
					labels: (data?.addLabels?.labels?.nodes || [])
						.map(makeTransformLabelToOption('newly_created'))
						.filter(({ value }) => option.value === value),
				});
			} catch (error) {
				experienceTracker.fail({
					error,
					name: LABELS_CREATE_LABEL_EXPERIENCE,
				});
			}
		},
		[experienceTracker, addLabel, labelData, fireLabelChangeEvent, contentId],
	);

	const handleDeleteLabel = useCallback(
		async (removedValue: any) => {
			const optimisticResponse = {
				deleteLabel: {
					__typename: 'DeleteLabelPayload',
					contentId,
					label: removedValue?.name,
				},
			};

			if (!removedValue) return;
			experienceTracker.start({
				name: LABELS_DELETE_LABEL_EXPERIENCE,
			});
			try {
				await deleteLabel({
					variables: {
						label: removedValue.name,
						contentId,
					},
				});
				experienceTracker.succeed({ name: LABELS_DELETE_LABEL_EXPERIENCE });
				fireLabelChangeEvent({
					action: 'deleted',
					labels: [removedValue],
				});
			} catch (error) {
				if (isLabelNotFoundError(error)) {
					markErrorAsHandled(error);
					handleDeleteLabelUpdate(getApolloClient(), {
						data: optimisticResponse,
					});
					experienceTracker.abort({
						name: LABELS_DELETE_LABEL_EXPERIENCE,
						reason: 'label already deleted',
					});
				} else if (isContentNotFoundError(error)) {
					markErrorAsHandled(error);
					experienceTracker.abort({
						name: LABELS_DELETE_LABEL_EXPERIENCE,
						reason: 'Content was deleted',
					});
				} else if (isLabelDeletePermissionsError(error)) {
					markErrorAsHandled(error);
					experienceTracker.abort({
						name: LABELS_DELETE_LABEL_EXPERIENCE,
						reason: 'Edit permissions were removed',
					});
				} else if (isLabelMutationLoggedOutError(error)) {
					markErrorAsHandled(error);
					experienceTracker.abort({
						name: LABELS_DELETE_LABEL_EXPERIENCE,
						reason: 'User was logged out',
					});
				} else {
					experienceTracker.fail({
						error,
						name: LABELS_DELETE_LABEL_EXPERIENCE,
					});
				}
			}
		},
		[experienceTracker, deleteLabel, fireLabelChangeEvent, contentId, handleDeleteLabelUpdate],
	);

	const handleSelectManagedLabel = useCallback(
		async (option: any) => {
			if (!option?.managedLabelId || !managedEntityAri || !managedOrgAri || !managedWorkspaceAri) {
				return;
			}
			setInputValue('');
			const labelId = option.managedLabelId as string;
			// Optimistically show the tag; reconcile or roll back once the request settles.
			optimisticallyApplyManagedLabel(contentId, { id: labelId, name: option.name });
			const success = await applyManagedLabel(
				labelId,
				managedEntityAri,
				managedOrgAri,
				managedWorkspaceAri,
			);
			if (success) {
				refetchAppliedManaged();
			} else {
				rollbackApplyManagedLabel(contentId, labelId);
			}
		},
		[
			applyManagedLabel,
			refetchAppliedManaged,
			contentId,
			managedEntityAri,
			managedOrgAri,
			managedWorkspaceAri,
		],
	);

	const handleRemoveManagedLabel = useCallback(
		async (removedValue: any) => {
			if (!removedValue?.managedLabelId) {
				return;
			}
			const labelId = removedValue.managedLabelId as string;
			// Optimistically hide the tag; reconcile or roll back once the request settles.
			optimisticallyRemoveManagedLabel(contentId, labelId);
			const success = await removeManagedLabel(labelId, removedValue.name);
			if (success) {
				refetchAppliedManaged();
			} else {
				rollbackRemoveManagedLabel(contentId, labelId);
			}
		},
		[removeManagedLabel, refetchAppliedManaged, contentId],
	);

	const handleLabelChange = async (_, { action, option, removedValue }) => {
		if (action === ActionTypes.SELECT_OPTION) {
			// The "No matches." placeholder row is non-selectable.
			if (option?.isNoMatch) {
				return;
			}
			if (option?.labelKind === 'managed') {
				void handleSelectManagedLabel(option);
			} else {
				void handleSelectLabel(option);
			}
		} else if (action === ActionTypes.CREATE_OPTION) {
			void handleCreateLabel(option);
		} else if (action === ActionTypes.REMOVE_VALUE || action === ActionTypes.POP_VALUE) {
			if (removedValue?.labelKind === 'managed') {
				void handleRemoveManagedLabel(removedValue);
			} else {
				void handleDeleteLabel(removedValue);
			}
		}
	};

	const handleMenuOpen = useCallback(() => {
		fetchRecommendedLabels();
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'open',
				actionSubject: 'labelsSelect',
				source,
				containerId: spaceId,
				containerType: 'space',
				objectId: contentId,
				objectType: type,
				attributes: {
					contentType: type,
				},
			},
		}).fire();
	}, [source, spaceId, contentId, type, createAnalyticsEvent, fetchRecommendedLabels]);

	const isLoading = searchLabelQueryLoading || recommendedLabelsQueryLoading;
	const currentLabelsQueryLoading = hasInheritedLabelsData
		? false
		: labelsQueryLoading || ssrLabelsQueryLoading;

	// Managed tags come from a separate read, so they render even while the
	// user-label query is still loading.
	const value = [...selectedManagedOptions, ...(!currentLabelsQueryLoading ? selectedOptions : [])];

	return {
		options: isLoading ? [] : options,
		value,
		filterOption,
		onChange: handleLabelChange,
		onMenuOpen: handleMenuOpen,
		onInputChange: handleInputChange,
		inputValue,
		isLoading,
		isDisabled,
		labelsQueryLoading: currentLabelsQueryLoading,
		labelsQueryError: hasInheritedLabelsData ? undefined : labelsQueryError || ssrLabelsQueryError,
		labelsMutationError: addError || deleteError,
		labelsSearchQueryError: searchLabelQueryError,
		validationError,
		fetchRecommendedLabels,
	};
};
